import React, { useState, useEffect } from "react";
import {
  Box, TextField, Button, Typography, IconButton, Alert, Paper,
  Divider, useTheme, Autocomplete, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow,
} from "@mui/material";
import { Close, Save, Add, Delete } from "@mui/icons-material";
import { useAuditFirestore } from "../../hooks/useAuditFirestore";
import { useCollection } from "../../hooks/useCollection";
import { createJournalEntryHeader, createJournalLine, validateDoubleEntry } from "../../utils/models/accounting/journalEntryModel";

const JournalEntryForm = ({ entry, onSave, onCancel }) => {
  const theme = useTheme();
  const { add, update } = useAuditFirestore("journalEntries");
  const { data: accounts } = useCollection("chartOfAccounts");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [header, setHeader] = useState(() => {
    if (entry) return { date: entry.date, concept: entry.concept, entryNumber: entry.entryNumber, isManual: true };
    return createJournalEntryHeader({ isManual: true });
  });

  const [lines, setLines] = useState(() => {
    if (entry?.lines) return entry.lines;
    return [
      createJournalLine(),
      createJournalLine(),
    ];
  });

  const activeAccounts = accounts?.filter((a) => a.isActive !== false) || [];

  const totals = lines.reduce(
    (acc, line) => ({
      debit: acc.debit + (parseFloat(line.debit) || 0),
      credit: acc.credit + (parseFloat(line.credit) || 0),
    }),
    { debit: 0, credit: 0 }
  );

  const isBalanced = Math.abs(totals.debit - totals.credit) < 0.001;

  const handleHeaderChange = (e) => {
    setHeader((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError("");
  };

  const handleLineChange = (lineId, field, value) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== lineId) return line;
        return { ...line, [field]: field === 'debit' || field === 'credit' ? parseFloat(value) || 0 : value };
      })
    );
  };

  const handleAccountSelect = (lineId, account) => {
    setLines((prev) =>
      prev.map((line) => {
        if (line.id !== lineId) return line;
        return {
          ...line,
          accountCode: account?.code || '',
          accountName: account?.name || '',
        };
      })
    );
  };

  const handleAddLine = () => {
    setLines((prev) => [...prev, createJournalLine()]);
  };

  const handleRemoveLine = (lineId) => {
    if (lines.length <= 2) return;
    setLines((prev) => prev.filter((l) => l.id !== lineId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!header.date) { setError("La fecha es obligatoria"); return; }
    if (!header.concept) { setError("El concepto es obligatorio"); return; }
    if (lines.some((l) => !l.accountCode)) { setError("Todas las líneas deben tener cuenta asignada"); return; }

    try {
      validateDoubleEntry(lines);
    } catch (err) {
      setError(err.message);
      return;
    }

    setLoading(true);
    setError("");
    try {
      const entryData = {
        ...header,
        lines,
        updatedAt: new Date().toISOString(),
      };
      if (entry?.id) {
        await update(entry.id, entryData);
      } else {
        await add({ ...entryData, createdAt: new Date().toISOString(), isActive: true });
      }
      onSave();
    } catch (err) {
      setError(err.message || "Error al guardar");
    } finally {
      setLoading(false);
    }
  };

  const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: "12px" } };

  return (
    <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: "24px", border: "1px solid", borderColor: theme.palette.divider }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h5" fontWeight={800}>{entry?.id ? "Editar Asiento" : "Nuevo Asiento Manual"}</Typography>
        <IconButton onClick={onCancel}><Close /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: "12px" }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
          <TextField label="Nº Asiento" name="entryNumber" value={header.entryNumber} onChange={handleHeaderChange} size="small" sx={{ ...fieldSx, flex: 1, minWidth: 150 }} />
          <TextField label="Fecha" name="date" type="date" value={header.date} onChange={handleHeaderChange} required size="small" InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, flex: 1, minWidth: 150 }} />
        </Box>
        <TextField label="Concepto" name="concept" value={header.concept} onChange={handleHeaderChange} required size="small" fullWidth sx={{ ...fieldSx, mb: 3 }} />

        <Divider sx={{ my: 3 }} />

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="subtitle1" fontWeight={700}>Líneas de Asiento</Typography>
          <Button startIcon={<Add />} onClick={handleAddLine} size="small" variant="outlined" sx={{ borderRadius: "10px" }}>Añadir línea</Button>
        </Box>

        <TableContainer sx={{ mb: 3, border: "1px solid", borderColor: theme.palette.divider, borderRadius: "12px" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}>
                <TableCell sx={{ fontWeight: 600 }}>Cuenta</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Descripción</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>Debe</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, width: 120 }}>Haber</TableCell>
                <TableCell sx={{ width: 50 }}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((line) => (
                <TableRow key={line.id}>
                  <TableCell sx={{ minWidth: 250 }}>
                    <Autocomplete
                      options={activeAccounts}
                      getOptionLabel={(opt) => `${opt.code} - ${opt.name}`}
                      value={activeAccounts.find((a) => a.code === line.accountCode) || null}
                      onChange={(_, value) => handleAccountSelect(line.id, value)}
                      renderInput={(params) => <TextField {...params} size="small" variant="standard" placeholder="Buscar cuenta..." />}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField value={line.description} onChange={(e) => handleLineChange(line.id, 'description', e.target.value)} size="small" variant="standard" fullWidth placeholder="Descripción" />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" value={line.debit || ''} onChange={(e) => handleLineChange(line.id, 'debit', e.target.value)} size="small" variant="standard" inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }} />
                  </TableCell>
                  <TableCell align="right">
                    <TextField type="number" value={line.credit || ''} onChange={(e) => handleLineChange(line.id, 'credit', e.target.value)} size="small" variant="standard" inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right' } }} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => handleRemoveLine(line.id)} disabled={lines.length <= 2}><Delete fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
          <Box sx={{ p: 2, borderRadius: "12px", border: "1px solid", borderColor: isBalanced ? theme.palette.success.main : theme.palette.error.main, bgcolor: isBalanced ? 'rgba(0,147,93,0.05)' : 'rgba(211,47,47,0.05)', minWidth: 250 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2">Total Debe</Typography>
              <Typography variant="body2" fontWeight={600}>{totals.debit.toFixed(2)}€</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
              <Typography variant="body2">Total Haber</Typography>
              <Typography variant="body2" fontWeight={600}>{totals.credit.toFixed(2)}€</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" fontWeight={600}>Diferencia</Typography>
              <Typography variant="body2" fontWeight={700} color={isBalanced ? 'success.main' : 'error.main'}>
                {Math.abs(totals.debit - totals.credit).toFixed(2)}€ {isBalanced ? '✓' : '✗'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button variant="outlined" onClick={onCancel} sx={{ borderRadius: "12px", textTransform: "none" }}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={loading} startIcon={<Save />} sx={{ borderRadius: "12px", textTransform: "none", boxShadow: "none" }}>
            {loading ? "Guardando..." : entry?.id ? "Actualizar" : "Crear Asiento"}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default JournalEntryForm;